import * as React from 'react';
import { graphql, compose } from 'react-apollo';
import { UpdateUserNoteInput } from 'globalTypes';

import ErrorActions from 'actions/ErrorActions';
import ToastActions from 'actions/ToastActions';
import LinkConstants from 'constants/LinkConstants';

import UsersProfile from './UsersProfile';
import UPDATE_USER_NOTE_MUTATION from './queries/UpdateUserNoteMutation.gql';
// @ts-ignore
import { EntityType } from 'components/PeopleList/PeopleList';
// @ts-ignore
import { GroupProfileType } from 'groups/components/GroupProfile';
import { UserProfile } from 'users/components/UsersProfile';
import { OrganizationMemberProfile_organizations_members_members } from './queries/__generated__/OrganizationMemberProfile';
import { errorFragment } from 'queries/__generated__/errorFragment';

import './UsersProfile.scss';
import LoadingIndicator from 'components/LoadingIndicator';
import Row from 'components/Row';

export type Props = {
  entityUuid: string;
  entityType?: EntityType;
  entityName?: string;
  member: UserProfile;
  loading: boolean;
  loaded: boolean;
  error: errorFragment;
  onBackClicked?: () => void;
  onFamilyMemberClicked: (user: UserProfile, role: string) => void;
  onHidePrivateNote?: () => void;
  onGroupClicked: (group: GroupProfileType) => void;
  onClose: () => void;
  onUserChanged: () => Promise<void>;
  reloadQuery: boolean;
  refetch: () => Promise<OrganizationMemberProfile_organizations_members_members>;
  mutate: (props: {
    variables: {
      input: UpdateUserNoteInput;
    };
  }) => Promise<any>;
  errorBanner: React.ReactNode;
  userActionsMenuButton: React.ReactNode;
  showPrivateNote?: boolean;
  trackEvent: (value: string, object?: Object) => void;
};

class UsersProfileContainer extends React.Component<Props> {
  componentDidUpdate(prevProps: Props) {
    const { reloadQuery, refetch, error, loading, member, onBackClicked, onClose } = this.props;

    if (reloadQuery && !prevProps.reloadQuery) {
      refetch();
    }

    if (error) {
      ErrorActions.serverError(error);
    }

    if (!loading && !member) {
      ToastActions.showErrorToast(
        `
          There was an error. Please try again, or email
          ${LinkConstants.SUPPORT_EMAIL}
          for help.
        `
      );

      if (onBackClicked) {
        onBackClicked();
      } else {
        onClose();
      }
    }
  }

  updateNote = (note: string): Promise<any> => {
    const { refetch, mutate, member, trackEvent } = this.props;

    trackEvent('user_profile.note.updated');

    return mutate({
      variables: {
        input: {
          userId: member.id,
          note,
        },
      },
    })
      .then(() => {
        return refetch().catch(e => {
          console.debug(e, e && e.stack);
          console.error('UsersProfileContainer - failed to refresh sidebar profile');
          ToastActions.showErrorToast('Oops, an error occured, could not refresh profile');
        });
      })
      .catch(e => {
        console.debug(e, e && e.stack);
        console.error('UsersProfileContainer - failed to update note');
        ToastActions.showErrorToast('Oops, an error occured, could not update note');
      });
  };

  render() {
    const {
      member,
      loading,
      loaded,
      onBackClicked,
      onFamilyMemberClicked,
      onGroupClicked,
      onHidePrivateNote,
      onUserChanged,
      onClose,
      trackEvent,
      userActionsMenuButton,
      entityUuid,
      entityType,
      entityName,
      showPrivateNote,
      refetch,
    } = this.props;

    if (!member) {
      return null;
    }

    if (loading || !loaded) {
      return <LoadingIndicator align="center" size="small" height={100} />;
    } else {
      return (
        <UsersProfile
          member={member}
          onBackClicked={onBackClicked}
          onClose={onClose}
          onFamilyMemberClicked={onFamilyMemberClicked}
          onGroupClicked={onGroupClicked}
          onHidePrivateNote={onHidePrivateNote}
          onNoteChange={this.updateNote}
          onUserChanged={onUserChanged}
          onPreferredLanguageUpdated={refetch}
          trackEvent={trackEvent}
          userActionsMenuButton={userActionsMenuButton}
          entityType={entityType}
          entityUuid={entityUuid}
          entityName={entityName}
          showPrivateNote={showPrivateNote}
        />
      );
    }
  }
}

const withUpdateUserNoteMutation = graphql(UPDATE_USER_NOTE_MUTATION);

export { UsersProfileContainer };

export default compose(withUpdateUserNoteMutation)(UsersProfileContainer);
